<script setup lang="ts">
import { ref } from 'vue';
import CInput from '@/components/shared/CInput';
import CButton from '@/components/shared/CButton';
import CalendarColorField from './CalendarColorField.vue';
import CalendarFieldTitle from './CalendarFieldTitle.vue';
import type { CalendarStrokeValue } from './types';

const DEFAULT_STROKE: CalendarStrokeValue = { width: '0.5', color: '#000000' };

const props = withDefaults(
    defineProps<{
        width?: string;
        color?: string;
        title?: string;
        disabled?: boolean;
    }>(),
    { width: '0', color: '#000000', title: 'Stroke' }
);

const emit = defineEmits<{
    (e: 'update', value: CalendarStrokeValue): void;
}>();

// Not derived from width so typing 0 doesn't collapse the fields; parent remounts on element change
const enabled = ref(Number(props.width) > 0);

const isTransparent = (color: string) => color === 'transparent' || /^#[0-9a-f]{6}00$/i.test(color);

const toggle = () => {
    enabled.value = !enabled.value;
    if (enabled.value) {
        emit('update', {
            width: DEFAULT_STROKE.width,
            color: isTransparent(props.color) ? DEFAULT_STROKE.color : props.color
        });
    } else {
        emit('update', { width: '0', color: props.color });
    }
};

const onWidthChange = (value: string | number = 0) => {
    emit('update', { width: String(value), color: props.color });
};

const onColorChange = (color: string) => {
    emit('update', { width: props.width, color });
};
</script>

<template>
    <section class="calendar-stroke-field">
        <template v-if="enabled">
            <calendar-field-title>{{ title }}</calendar-field-title>
            <c-input
                :value="Number(width)"
                type="decimal"
                step=".1"
                :min="0"
                label="Width"
                :disabled="disabled"
                @input="onWidthChange"
            />
            <calendar-color-field :value="color" :disabled="disabled" @input="onColorChange" />
        </template>
        <c-button
            class="w-100"
            variant="secondary"
            :text="enabled ? 'Remove Stroke' : 'Add Stroke'"
            :icon="enabled ? ['far', 'trash'] : ['far', 'plus']"
            :disabled="disabled"
            @click="toggle"
        />
    </section>
</template>

<style scoped lang="scss">
.calendar-stroke-field {
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
}
</style>
